import type {
  FreeOptionConfig,
  ModifierGroup,
  ModifierOption,
  ModifierOptionType,
  SelectionIndicator,
  SelectionRule,
} from "./types"

/**
 * Item option selection rules — a TypeScript port of itd_cater_app's
 * `ItemOptionSelectionEngine` (lib/src/features/till/utils/
 * item_option_selection.dart) and `resolveItemOptionFreeUnits`
 * (item_option_free_resolver.dart), contract v0.28.0. Test vectors in
 * item-option-selection.test.ts are transcribed from the Dart tests.
 *
 * Lives in `model` (not `ordering`) because BOTH sides need the exact same
 * rules: the `"use client"` customizer drives its taps through it, and the
 * server-side pricing engine (`@workspace/ordering`) prices the resulting picks.
 * It knows nothing about money — it only decides WHICH units are picked and
 * which of them a free allowance covers. Pure, no I/O.
 *
 * DEVIATIONS from the Dart source, all forced by the storefront contract:
 *  - one `ModifierGroup` carries its own rule/max/free config (the till reads
 *    them from the variety config), so every function takes the group;
 *  - an absent `selection.max` is UNLIMITED (the backend already maps the
 *    till's "missing ⇒ 1" to an explicit `max: 1`);
 *  - options carry `available`: an unavailable option can't be tapped, implied
 *    by a select-all, defaulted or auto-added.
 *  - the group's options include its subgroups' (flattened, display order).
 *  - a select-all implies multiSelect options too, as ONE unit (the till only
 *    implies simple ones); further units the diner adds on top are explicit,
 *    counted and priced as usual.
 */

/** One picked unit. A multiSelect option picked three times is three picks. */
export interface OptionPick {
  optionId: string
  /** Set when the pick was implied by the allSelect option with this id. */
  includedInOptionId?: string
}

/** Picks per group, in tap order; index = the group's position in the item's `modifierGroups`. */
export type OptionSelection = readonly (readonly OptionPick[])[]

export interface OptionTapResult {
  picks: OptionPick[]
  /** The tap was refused because the group's max is used up. */
  maxReached: boolean
}

/** What the customizer renders for one option. */
export interface OptionTileState {
  option: ModifierOption
  /** Effective type in this group (see {@link effectiveOptionType}). */
  type: ModifierOptionType
  /** Units picked explicitly by the diner. */
  quantity: number
  /** How many of `quantity` cost nothing. */
  freeUnits: number
  /** Picked only because a select-all option is on. */
  isIncluded: boolean
  /** Unpicked and the group's max is reached. */
  isBlocked: boolean
  /** Picking another unit now would be free. */
  nextUnitFree: boolean
  /**
   * Tapping it now re-includes it under the active select-all (priced 0) —
   * an option the diner took back out of "Everything".
   */
  nextPickIncluded: boolean
  isSelected: boolean
  /** multiSelect options drop one unit at a time. */
  canDecrement: boolean
  /** Some picked units are free, or the next pick of an unpicked option would be. */
  showsFreeBadge: boolean
  /** Units the price label covers: the paid units once picked, else the one unit the next pick adds. */
  displayPaidUnits: number
}

/**
 * One selected-option ROW, in the till's `toSelectedOptions` order: group then
 * menu order; free and paid units of one option are separate rows (free first),
 * and options implied by a select-all follow it.
 */
export interface OptionSelectionRow {
  option: ModifierOption
  /** Units per unit of the line. */
  quantity: number
  /** Covered by the group's free allowance (priced 0). */
  free: boolean
  /** Implied by this allSelect option (priced 0). */
  includedInOptionId?: string
}

/** A group's options, subgroups flattened in display order. */
export function groupOptionList(group: ModifierGroup): ModifierOption[] {
  return [
    ...(group.options ?? []),
    ...(group.subgroups?.flatMap((subgroup) => subgroup.options) ?? []),
  ]
}

function findOption(
  group: ModifierGroup,
  optionId: string
): ModifierOption | undefined {
  return groupOptionList(group).find((option) => option.id === optionId)
}

const isSingle = (group: ModifierGroup) => group.selection.kind === "single"

/** Absent or unknown ⇒ radio for single, checkbox for multi. */
export function resolveSelectionIndicator(
  rule: SelectionRule
): SelectionIndicator {
  switch (rule.indicator) {
    case "radio":
    case "checkbox":
    case "none":
      return rule.indicator
    default:
      return rule.kind === "single" ? "radio" : "checkbox"
  }
}

/** The diner must pick before confirming: `required`, or a positive `min`. */
export function isSelectionRequired(rule: SelectionRule): boolean {
  return Boolean(rule.required) || (rule.min ?? 0) > 0
}

/**
 * The type the rules apply to `option` in `group`: allSelect and multiSelect
 * make no sense in a single group, so they act as simple there. Unknown ⇒ simple.
 */
export function effectiveOptionType(
  group: ModifierGroup,
  option: ModifierOption
): ModifierOptionType {
  const type = option.optionType
  if (type !== "allSelect" && type !== "noSelect" && type !== "multiSelect") {
    return "simple"
  }
  if (isSingle(group) && (type === "allSelect" || type === "multiSelect")) {
    return "simple"
  }
  return type
}

/** Whether picking `option` counts toward the group's max. */
export function countsTowardMax(
  group: ModifierGroup,
  option: ModifierOption
): boolean {
  const type = effectiveOptionType(group, option)
  return type === "simple" || type === "multiSelect"
}

/** Whether a select-all implies `option` (one unit, priced 0). */
function impliedBySelectAll(
  group: ModifierGroup,
  option: ModifierOption
): boolean {
  const type = effectiveOptionType(group, option)
  return type === "simple" || type === "multiSelect"
}

/** Max counted units, or undefined when unlimited. Single groups are capped at 1. */
export function groupMaxUnits(group: ModifierGroup): number | undefined {
  if (isSingle(group)) return 1
  const max = group.selection.max
  return max != null && max > 0 ? max : undefined
}

/** Picks counting toward the group's max. */
export function countedUnits(
  group: ModifierGroup,
  picks: readonly OptionPick[]
): number {
  return picks.filter((pick) => {
    if (pick.includedInOptionId) return false
    const option = findOption(group, pick.optionId)
    return option != null && countsTowardMax(group, option)
  }).length
}

export function isGroupAtMax(
  group: ModifierGroup,
  picks: readonly OptionPick[]
): boolean {
  const max = groupMaxUnits(group)
  return max != null && countedUnits(group, picks) >= max
}

/**
 * Whether the group holds a pick, or doesn't need one. A positive `min` (not a
 * till concept, kept from the original contract) additionally needs that many
 * counted units unless the diner chose the group's "None".
 */
export function isGroupSatisfied(
  group: ModifierGroup,
  picks: readonly OptionPick[]
): boolean {
  const rule = group.selection
  if (rule.required && picks.length === 0) return false
  const min = rule.min ?? 0
  if (min <= 0) return true
  const hasNone = picks.some((pick) => {
    const option = findOption(group, pick.optionId)
    return option != null && effectiveOptionType(group, option) === "noSelect"
  })
  return hasNone || countedUnits(group, picks) >= min
}

/** Index of the first group the diner still has to answer, or undefined when the selection can be confirmed. */
export function firstUnsatisfiedGroupIndex(
  groups: readonly ModifierGroup[],
  selection: OptionSelection
): number | undefined {
  const index = groups.findIndex(
    (group, i) => !isGroupSatisfied(group, selection[i] ?? [])
  )
  return index < 0 ? undefined : index
}

// ── Free units ─────────────────────────────────────────────────────────────

function isFreeConfigActive(
  config: FreeOptionConfig | undefined
): config is FreeOptionConfig {
  return (config?.freeCount ?? 0) > 0
}

/**
 * How many units of each picked option are free in one group (port of
 * `resolveItemOptionFreeUnits`). `unitOptionIds` lists one entry per picked
 * unit in tap order; options implied by a select-all must not be passed in.
 *
 * Free slots go to the first `freeCount` units of eligible options — the
 * config's `freeOptionIds`, or every id in `groupOptionIds` when that list is
 * empty. With `allowSwap`, leftover slots go to the first non-eligible units.
 * Returns optionId → free units (entries above 0 only).
 */
export function resolveGroupFreeUnits(
  config: FreeOptionConfig | undefined,
  unitOptionIds: readonly string[],
  groupOptionIds: ReadonlySet<string>
): Map<string, number> {
  const free = new Map<string, number>()
  if (!isFreeConfigActive(config) || unitOptionIds.length === 0) return free

  const configured = config.freeOptionIds ?? []
  const pool = configured.length > 0 ? new Set(configured) : groupOptionIds
  let budget = config.freeCount ?? 0

  const grant = (eligible: (optionId: string) => boolean) => {
    for (const optionId of unitOptionIds) {
      if (budget <= 0) return
      if (!eligible(optionId)) continue
      free.set(optionId, (free.get(optionId) ?? 0) + 1)
      budget--
    }
  }

  grant((id) => pool.has(id))
  if (config.allowSwap) grant((id) => !pool.has(id))
  return free
}

/** optionId → free units for the group's explicit picks, under `group.freeOption`. */
export function groupFreeUnits(
  group: ModifierGroup,
  picks: readonly OptionPick[]
): Map<string, number> {
  return resolveGroupFreeUnits(
    group.freeOption,
    picks.filter((pick) => !pick.includedInOptionId).map((p) => p.optionId),
    new Set(groupOptionList(group).map((option) => option.id))
  )
}

// ── Taps ───────────────────────────────────────────────────────────────────

function withoutNoSelect(
  group: ModifierGroup,
  picks: readonly OptionPick[]
): OptionPick[] {
  return picks.filter((pick) => {
    const option = findOption(group, pick.optionId)
    return option == null || effectiveOptionType(group, option) !== "noSelect"
  })
}

function withoutOptionAndChildren(
  picks: readonly OptionPick[],
  optionId: string
): OptionPick[] {
  return picks.filter(
    (pick) => pick.optionId !== optionId && pick.includedInOptionId !== optionId
  )
}

function activeAllSelectId(
  group: ModifierGroup,
  picks: readonly OptionPick[]
): string | undefined {
  for (const pick of picks) {
    if (pick.includedInOptionId) continue
    const option = findOption(group, pick.optionId)
    if (option && effectiveOptionType(group, option) === "allSelect") {
      return option.id
    }
  }
  return undefined
}

function appendCounted(
  group: ModifierGroup,
  original: readonly OptionPick[],
  base: OptionPick[],
  optionId: string
): OptionTapResult {
  if (isGroupAtMax(group, base)) {
    return { picks: [...original], maxReached: true }
  }
  return { picks: [...base, { optionId }], maxReached: false }
}

/** Applies one tap on `optionId` to a group's picks (port of `ItemOptionSelectionEngine.tap`). */
export function tapOption(
  group: ModifierGroup,
  picks: readonly OptionPick[],
  optionId: string
): OptionTapResult {
  const unchanged = { picks: [...picks], maxReached: false }
  const option = findOption(group, optionId)
  if (!option || !option.available) return unchanged

  if (isSingle(group)) {
    // Radio: another option replaces the pick; re-tapping the current one
    // clears it unless the group is required.
    if (picks.some((pick) => pick.optionId === optionId)) {
      return group.selection.required
        ? unchanged
        : { picks: [], maxReached: false }
    }
    return { picks: [{ optionId }], maxReached: false }
  }

  const explicitlyPicked = picks.some(
    (pick) => pick.optionId === optionId && !pick.includedInOptionId
  )
  const base = withoutNoSelect(group, picks)

  switch (effectiveOptionType(group, option)) {
    case "noSelect":
      return {
        picks: explicitlyPicked ? [] : [{ optionId }],
        maxReached: false,
      }

    case "allSelect": {
      if (explicitlyPicked) {
        return {
          picks: withoutOptionAndChildren(picks, optionId),
          maxReached: false,
        }
      }
      const pickedIds = new Set(base.map((pick) => pick.optionId))
      return {
        picks: [
          ...base,
          { optionId },
          ...groupOptionList(group)
            .filter(
              (other) =>
                other.id !== optionId &&
                other.available &&
                impliedBySelectAll(group, other) &&
                !pickedIds.has(other.id)
            )
            .map((other) => ({
              optionId: other.id,
              includedInOptionId: optionId,
            })),
        ],
        maxReached: false,
      }
    }

    case "simple": {
      if (picks.some((pick) => pick.optionId === optionId)) {
        return {
          picks: picks.filter((pick) => pick.optionId !== optionId),
          maxReached: false,
        }
      }
      const allSelectId = activeAllSelectId(group, base)
      if (allSelectId) {
        // Re-include an option the diner had taken out of "everything".
        return {
          picks: [...base, { optionId, includedInOptionId: allSelectId }],
          maxReached: false,
        }
      }
      return appendCounted(group, picks, base, optionId)
    }

    case "multiSelect": {
      const allSelectId = picks.some((pick) => pick.optionId === optionId)
        ? undefined
        : activeAllSelectId(group, base)
      if (allSelectId) {
        // Re-include it as its one implied unit; later taps add paid units.
        return {
          picks: [...base, { optionId, includedInOptionId: allSelectId }],
          maxReached: false,
        }
      }
      return appendCounted(group, picks, base, optionId)
    }
  }
}

/**
 * Removes one unit of `optionId`; removing a select-all also removes what it
 * implied. Explicit units go first, then the unit a select-all implied (a
 * multiSelect option taken back out of "everything").
 */
export function decrementOption(
  picks: readonly OptionPick[],
  optionId: string
): OptionPick[] {
  let index = -1
  for (let i = picks.length - 1; i >= 0; i--) {
    const pick = picks[i]
    if (pick && pick.optionId === optionId && !pick.includedInOptionId) {
      index = i
      break
    }
  }
  if (index < 0) {
    return picks.filter((pick) => pick.optionId !== optionId)
  }
  const next = picks.filter((_, i) => i !== index)
  const stillPicked = next.some(
    (pick) => pick.optionId === optionId && !pick.includedInOptionId
  )
  return stillPicked
    ? next
    : next.filter((pick) => pick.includedInOptionId !== optionId)
}

// ── Initial state ──────────────────────────────────────────────────────────

/**
 * The group's configured defaults: `defaultOptionIds` when present (even
 * empty — authoritative), else the `defaultSelected` flags. Available options
 * only, in the group's option order.
 */
export function groupDefaultOptionIds(group: ModifierGroup): string[] {
  const options = groupOptionList(group).filter((option) => option.available)
  const configured = group.defaultOptionIds
  return configured
    ? options.filter((o) => configured.includes(o.id)).map((o) => o.id)
    : options.filter((o) => o.defaultSelected).map((o) => o.id)
}

function seedFreePicks(
  group: ModifierGroup,
  picks: OptionPick[]
): OptionPick[] {
  const free = group.freeOption
  if (!isFreeConfigActive(free) || !free.autoAdd) return picks
  // A filled single group or a "None" pick means the defaults already say
  // what this group should hold.
  const hasExclusivePick = picks.some((pick) => {
    const option = findOption(group, pick.optionId)
    return (
      isSingle(group) ||
      (option != null && effectiveOptionType(group, option) === "noSelect")
    )
  })
  if (hasExclusivePick) return picks

  const pool = new Set(free.freeOptionIds ?? [])
  const candidates = groupOptionList(group).filter(
    (option) =>
      option.available &&
      (pool.size === 0 || pool.has(option.id)) &&
      countsTowardMax(group, option)
  )

  let current = picks
  const freeCount = free.freeCount ?? 0
  for (const option of candidates) {
    const used = [...groupFreeUnits(group, current).values()].reduce(
      (sum, n) => sum + n,
      0
    )
    if (used >= freeCount) break
    if (current.some((pick) => pick.optionId === option.id)) continue
    const result = tapOption(group, current, option.id)
    if (result.maxReached) break
    current = result.picks
    if (isSingle(group)) break
  }
  return current
}

/** Defaults first (menu order), then eligible free options when the group auto-adds them, up to the free count. */
export function initialGroupPicks(group: ModifierGroup): OptionPick[] {
  let picks: OptionPick[] = []
  for (const optionId of groupDefaultOptionIds(group)) {
    if (picks.some((pick) => pick.optionId === optionId)) continue
    if (isSingle(group) && picks.length > 0) break
    picks = tapOption(group, picks, optionId).picks
  }
  return seedFreePicks(group, picks)
}

export function initialOptionSelection(
  groups: readonly ModifierGroup[]
): OptionPick[][] {
  return groups.map((group) => initialGroupPicks(group))
}

// ── Read models ────────────────────────────────────────────────────────────

/** Per-option render state for one group (port of `ItemOptionSelectionEngine.tiles`). */
export function optionTiles(
  group: ModifierGroup,
  picks: readonly OptionPick[]
): OptionTileState[] {
  const free = groupFreeUnits(group, picks)
  const allSelectId = isSingle(group)
    ? undefined
    : activeAllSelectId(group, picks)
  const blocked = isGroupAtMax(group, picks) && allSelectId == null
  const freeActive = isFreeConfigActive(group.freeOption)

  return groupOptionList(group).map((option) => {
    const quantity = picks.filter(
      (pick) => pick.optionId === option.id && !pick.includedInOptionId
    ).length
    const isIncluded = picks.some(
      (pick) => pick.optionId === option.id && pick.includedInOptionId != null
    )
    const type = effectiveOptionType(group, option)
    const freeUnits = free.get(option.id) ?? 0
    const canAddUnit = quantity === 0 || type === "multiSelect"

    let nextUnitFree = false
    // An implied multiSelect unit still takes paid units on top.
    if (
      freeActive &&
      (!isIncluded || type === "multiSelect") &&
      canAddUnit &&
      option.available
    ) {
      const result = tapOption(group, picks, option.id)
      if (!result.maxReached) {
        const after = groupFreeUnits(group, result.picks).get(option.id) ?? 0
        nextUnitFree = after > freeUnits
      }
    }

    return {
      option,
      type,
      quantity,
      freeUnits,
      isIncluded,
      isBlocked:
        !isSingle(group) &&
        blocked &&
        quantity === 0 &&
        !isIncluded &&
        countsTowardMax(group, option),
      nextUnitFree,
      nextPickIncluded:
        allSelectId != null &&
        quantity === 0 &&
        !isIncluded &&
        option.available &&
        impliedBySelectAll(group, option),
      isSelected: quantity > 0 || isIncluded,
      canDecrement: type === "multiSelect" && (quantity > 0 || isIncluded),
      showsFreeBadge: freeUnits > 0 || (quantity === 0 && nextUnitFree),
      displayPaidUnits: quantity === 0 ? 1 : quantity - freeUnits,
    }
  })
}

/** The group's picks as selected-option rows (port of `toSelectedOptions`, one group). */
export function groupSelectionRows(
  group: ModifierGroup,
  picks: readonly OptionPick[]
): OptionSelectionRow[] {
  if (picks.length === 0) return []
  const free = groupFreeUnits(group, picks)
  const options = groupOptionList(group)
  const rows: OptionSelectionRow[] = []

  for (const option of options) {
    const quantity = picks.filter(
      (pick) => pick.optionId === option.id && !pick.includedInOptionId
    ).length
    if (quantity === 0) continue

    const freeQuantity = Math.min(
      Math.max(free.get(option.id) ?? 0, 0),
      quantity
    )
    if (freeQuantity > 0) {
      rows.push({ option, quantity: freeQuantity, free: true })
    }
    if (quantity > freeQuantity) {
      rows.push({ option, quantity: quantity - freeQuantity, free: false })
    }

    for (const child of options) {
      const included = picks.some(
        (pick) =>
          pick.optionId === child.id && pick.includedInOptionId === option.id
      )
      if (included) {
        rows.push({
          option: child,
          quantity: 1,
          free: false,
          includedInOptionId: option.id,
        })
      }
    }
  }
  return rows
}

// ── Untrusted input ────────────────────────────────────────────────────────

/**
 * Re-validates a group's stored picks against the LIVE group (a cart cookie is
 * untrusted and the menu may have changed since): unknown/unavailable options
 * and orphaned implied picks are dropped, a single group keeps one pick, a
 * "None" pick stays exclusive, and counted units beyond `max` are dropped.
 */
export function normalizeGroupPicks(
  group: ModifierGroup,
  raw: readonly OptionPick[]
): OptionPick[] {
  const known = raw.filter((pick) => {
    const option = findOption(group, pick.optionId)
    return option != null && option.available
  })
  const explicit = known.filter((pick) => !pick.includedInOptionId)

  if (isSingle(group)) {
    const first = explicit[0]
    return first ? [{ optionId: first.optionId }] : []
  }

  const none = explicit.find((pick) => {
    const option = findOption(group, pick.optionId)
    return option != null && effectiveOptionType(group, option) === "noSelect"
  })
  if (none) return [{ optionId: none.optionId }]

  const result: OptionPick[] = []
  const max = groupMaxUnits(group)
  let counted = 0
  for (const pick of known) {
    const option = findOption(group, pick.optionId)
    if (!option) continue
    const type = effectiveOptionType(group, option)
    if (pick.includedInOptionId) {
      // Implied picks only stand under an explicitly picked select-all, once
      // per option, and only for simple / multiSelect options (a simple one
      // not also picked explicitly — see the filter below).
      const parent = findOption(group, pick.includedInOptionId)
      const parentPicked = explicit.some(
        (p) => p.optionId === pick.includedInOptionId
      )
      const duplicate = result.some(
        (p) =>
          p.optionId === pick.optionId &&
          (type !== "multiSelect" || p.includedInOptionId != null)
      )
      if (
        parent &&
        parentPicked &&
        effectiveOptionType(group, parent) === "allSelect" &&
        impliedBySelectAll(group, option) &&
        !duplicate
      ) {
        result.push({
          optionId: pick.optionId,
          includedInOptionId: pick.includedInOptionId,
        })
      }
      continue
    }
    const already = result.some(
      (p) => p.optionId === pick.optionId && !p.includedInOptionId
    )
    if (already && type !== "multiSelect") continue
    if (countsTowardMax(group, option)) {
      if (max != null && counted >= max) continue
      counted++
    }
    result.push({ optionId: pick.optionId })
  }
  // An implied simple pick listed before an explicit pick of the same option
  // loses; a multiSelect one stays as the option's first unit.
  return result.filter((pick) => {
    if (!pick.includedInOptionId) return true
    const option = findOption(group, pick.optionId)
    return (
      (option != null &&
        effectiveOptionType(group, option) === "multiSelect") ||
      !result.some((p) => p.optionId === pick.optionId && !p.includedInOptionId)
    )
  })
}

/** {@link normalizeGroupPicks} over every group of an item. */
export function normalizeOptionSelection(
  groups: readonly ModifierGroup[],
  raw: OptionSelection
): OptionPick[][] {
  return groups.map((group, index) =>
    normalizeGroupPicks(group, raw[index] ?? [])
  )
}

/**
 * Legacy flat option ids (old cart cookies, re-order, meal-deal varieties) as a
 * selection: each id is CONSUMED by the first group occurrence holding it, so an
 * id selected twice lands in a second group sharing it (the live menu reuses
 * option ids across groups) — the same attribution `resolveModifierSelections`
 * (@workspace/ordering) has always applied.
 */
export function optionSelectionFromIds(
  groups: readonly ModifierGroup[],
  selectedOptionIds: readonly string[]
): OptionPick[][] {
  const remaining = new Map<string, number>()
  for (const id of selectedOptionIds) {
    remaining.set(id, (remaining.get(id) ?? 0) + 1)
  }
  return groups.map((group) => {
    const picks: OptionPick[] = []
    for (const option of groupOptionList(group)) {
      const count = remaining.get(option.id) ?? 0
      if (count <= 0) continue
      remaining.set(option.id, count - 1)
      picks.push({ optionId: option.id })
    }
    return picks
  })
}
